import React, { useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDrawer, { KuikaDrawerPlacement } from "../../../../ui-kit/kuika-drawer/kuika-drawer";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

interface IDrawerDemoProps {
  placement: KuikaDrawerPlacement;
  triggerLabel: string;
  bodyLines: number;
}

const DrawerDemo: React.FC<IDrawerDemoProps> = ({ placement, triggerLabel, bodyLines }) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <KuikaButton type="secondary" size="md" onClick={() => setOpen(true)}>
        {triggerLabel}
      </KuikaButton>
      <KuikaDrawer
        isOpen={open}
        onClose={() => setOpen(false)}
        header="Project definition"
        placement={placement}
        footer={
          <>
            <KuikaButton type="secondary" size="md" onClick={() => setOpen(false)}>
              Cancel
            </KuikaButton>
            <KuikaButton type="primary" size="md" onClick={() => setOpen(false)}>
              Save
            </KuikaButton>
          </>
        }
      >
        {Array.from({ length: bodyLines }).map((_unused: unknown, index: number): React.ReactNode => (
          <p key={index} style={{ margin: "0 0 12px" }}>
            Body content line {index + 1} — the drawer opens with a long rise, a blurred backdrop and its content
            trailing in slightly after the panel.
          </p>
        ))}
      </KuikaDrawer>
    </>
  );
};

const NESTED_DRAWER_WIDTH: number = 256;
const NESTED_DRAWER_DEPTH: number = 3;

interface INestedDrawerLevelProps {
  level: number;
}

// Mirrors the action panel's object parameters: each level opens the next from inside itself, and
// the one beneath steps aside by its own width so the stack stays readable.
const NestedDrawerLevel: React.FC<INestedDrawerLevelProps> = ({ level }) => {
  const [childOpen, setChildOpen] = useState<boolean>(false);
  const hasChild: boolean = level < NESTED_DRAWER_DEPTH;

  return (
    <>
      <p style={{ margin: "0 0 12px" }}>Level {level}</p>
      {hasChild && (
        <>
          <KuikaButton type="secondary" size="sm" onClick={() => setChildOpen(true)}>
            Open level {level + 1}
          </KuikaButton>
          <KuikaDrawer
            isOpen={childOpen}
            onClose={() => setChildOpen(false)}
            header={`Level ${level + 1}`}
            width={NESTED_DRAWER_WIDTH}
            modal={false}
            maskClosable
            push={NESTED_DRAWER_WIDTH}
          >
            <NestedDrawerLevel level={level + 1} />
          </KuikaDrawer>
        </>
      )}
    </>
  );
};

const NestedDrawerDemo: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <KuikaButton type="secondary" size="md" onClick={() => setOpen(true)}>
        Open a nested stack
      </KuikaButton>
      <KuikaDrawer
        isOpen={open}
        onClose={() => setOpen(false)}
        header="Level 1"
        width={NESTED_DRAWER_WIDTH}
        modal={false}
        maskClosable
        push={NESTED_DRAWER_WIDTH}
      >
        <NestedDrawerLevel level={1} />
      </KuikaDrawer>
    </>
  );
};

export const DrawerStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Drawer"
      componentName="KuikaDrawer"
      sourcePath="ui-kit/kuika-drawer"
      description="Side panel on a Radix Dialog: a long, eased entrance with a blurred backdrop and a short exit, header/body/footer regions, four placements."
    >
      <SpecRow label="Right placement" caption="default — opens from the right edge">
        <DrawerDemo placement="right" triggerLabel="Open drawer" bodyLines={2} />
      </SpecRow>
      <SpecRow label="Left placement" caption='placement="left"'>
        <DrawerDemo placement="left" triggerLabel="Open left drawer" bodyLines={2} />
      </SpecRow>
      <SpecRow
        label="Nested, non-modal"
        caption="modal={false} + push — a drawer opened inside another pushes it aside; the page behind stays live for popups mounted on the body"
      >
        <NestedDrawerDemo />
      </SpecRow>
      <SpecRow label="Long body" caption="scrolling body region, sticky header and footer">
        <DrawerDemo placement="right" triggerLabel="Open long drawer" bodyLines={24} />
      </SpecRow>
    </ShowcaseFrame>
  );
};
